import { useEffect, useRef, useState } from 'react'
import { PressScale } from '../../lib/PressScale'

/**
 * DangerConfirm — the inline two-step for something irreversible-feeling
 * (remove a member, hand over ownership, leave the company).
 *
 * ── WHY INLINE AND NOT A MODAL ────────────────────────────────────────────
 * Because a modal over a modal is how a dead end gets built. These actions
 * live inside a row that is already expanded inside a panel; raising a third
 * layer means a third Escape handler and a third focus trap, and the last
 * review round found a shipped overlay whose only control signed the user out.
 * Confirming in place keeps the way back visible the entire time: "Cancel" is
 * always rendered, always enabled, and is the DEFAULT FOCUS.
 *
 * ── THE BUTTON LABEL STATES THE CONSEQUENCE ───────────────────────────────
 * "Remove Ravi from Kiran Gems", never "Confirm". A confirm dialog whose
 * button says "Confirm" is a dialog nobody reads, and this is the one control
 * on the surface that cannot be undone from the surface.
 *
 * `confirmations={2}` is the double-confirm for ownership transfer: the second
 * step re-states what changes about the CURRENT owner ("you become an admin"),
 * because that is the half people do not expect.
 */
export function DangerConfirm({
  prompt,
  confirmLabel,
  secondPrompt,
  secondConfirmLabel,
  busy = false,
  error,
  onConfirm,
  onCancel,
  testId,
}: {
  /** What is about to happen, in a sentence. */
  prompt: string
  /** Names the consequence. Never "Confirm". */
  confirmLabel: string
  /** Present ⇒ double-confirm. The second screen's sentence. */
  secondPrompt?: string
  secondConfirmLabel?: string
  busy?: boolean
  /** The server's own message, rendered in place — never a toast. A toast
   *  disappears before a person has read why they were refused. */
  error?: string | null
  onConfirm: () => void
  onCancel: () => void
  testId?: string
}) {
  const [step, setStep] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)

  // Cancel takes focus, on every step. The safe control is the default one.
  // Queried rather than ref-forwarded: `PressScale` is a motion component and
  // a forwarded ref through it is a refactor hazard for a two-line need.
  useEffect(() => {
    rootRef.current?.querySelector<HTMLButtonElement>('[data-role="danger-cancel"]')?.focus()
  }, [step])

  const twoStep = !!secondPrompt
  const onSecond = twoStep && step === 1
  const text = onSecond ? secondPrompt : prompt
  const label = onSecond ? (secondConfirmLabel ?? confirmLabel) : confirmLabel

  return (
    <div
      ref={rootRef}
      className="rounded-md border border-danger/30 bg-danger/[0.04] p-3"
      role="group"
      aria-label={prompt}
      data-testid={testId}
    >
      <p className="text-[13px] leading-relaxed text-ink">{text}</p>
      {error && (
        <p className="mt-2 text-[13px] font-medium text-danger" role="alert" data-testid={testId ? `${testId}-error` : undefined}>
          {error}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <PressScale
          feel="tap"
          onClick={onCancel}
          className="btn-secondary h-11 px-4 text-[13px]"
          data-role="danger-cancel"
          data-testid={testId ? `${testId}-cancel` : undefined}
        >
          Cancel
        </PressScale>
        <PressScale
          feel="deny"
          disabled={busy}
          onClick={() => {
            if (twoStep && step === 0) { setStep(1); return }
            onConfirm()
          }}
          className="inline-flex h-11 items-center justify-center rounded-full bg-danger px-4 text-[13px] font-semibold text-white disabled:opacity-50"
          data-testid={testId ? `${testId}-confirm` : undefined}
        >
          {busy ? 'Working…' : label}
        </PressScale>
      </div>
    </div>
  )
}
